import React, { FunctionComponent, useCallback, useEffect, useState, useRef } from 'react';
import CustomizeCarContext from '../context/CustomizeCarContext';
import { GetCustomizeCar_customizeCar } from '../graphql/queries/__generated__/GetCustomizeCar';
import useCustomizeCar from '../hooks/queries/useCustomizeCar';
import useSearchCarContext from '../hooks/useSearchCarContext';
import useUrlSearchParams from '../hooks/useUrlSearchParams';
import { FinanceFormInput, FinanceResultType, InsuranceFormInput } from '../../__generated__/globalTypes';
import { areEqual } from '../utils/areEqual';
import useGetPostCode from '../../gqlCommon/hooks/useGetPostCode';
import { useFinanceFormInput } from '../../gqlCommon/hooks/useFinanceFormInput';
import { getColorFeatureCodes } from '../utils/featureCategoryHelpers';
import { getCarId, createBasePath } from '../utils/carCodeHelpers';
import useStockLockContext from '../context/stockLock/useStockLockContext';
import stockLockService from '../service/stockLockService';
import { UrlSearchParamType } from '../enum/UrlSearchParamType';
import useInsuranceFormInput from '../../gqlCommon/hooks/useInsuranceFormInput';
import useSalesEnablementContext from '../../gqlCommon/hooks/useSalesEnablementContext';
import useWidgetContext from '../../common/react/useWidgetContext';
import { nextFeatureSelectionIdVar } from '../../gqlCommon/graphql/cache';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { useReactiveVar } from '@apollo/client';

const deepEqual = require('fast-deep-equal/es6');

const CustomizeCarContextProvider: FunctionComponent = ({ children }) => {
  const { setShowStockLockModal } = useStockLockContext();
  const {
    configuration: { basePath },
  } = useWidgetContext();
  const intl = useIntl();
  const [selectedCar, setSelectedCar] = useState<GetCustomizeCar_customizeCar>();
  const [error, setError] = useState<unknown>(null);
  const { isInSalesEnablement } = useSalesEnablementContext();
  const { searchCar, loading: isSeachCarLoading } = useSearchCarContext();
  const { searchParams, syncUrlSearchParams, createVehicleConfigurationUrl } = useUrlSearchParams();
  const postCode = useGetPostCode();
  const { data, error: customizeCarError, loading, loadCustomizeCar } = useCustomizeCar();
  const [optimisticSelectedFeatures, setOptimisticSelectedFeatures] = useState<string[]>(
    (searchParams?.selectedFeatures as string[]) || [],
  );
  const { insuranceFormData } = useInsuranceFormInput(selectedCar?.car.code);
  const { financeInput, financeFormInput } = useFinanceFormInput();
  const [initialFinanceFormInput, setInitialFinanceFormInput] = useState<FinanceFormInput>(financeFormInput);
  const [currentInsuranceFormInput, setCurrentInsuranceFormInput] = useState<InsuranceFormInput>(insuranceFormData);
  const currentSelectedFeatures = useRef<string[]>(data?.customizeCar?.selectedFeatures.selected);
  const currentCarCode = useRef<string>(data?.customizeCar?.car.code);
  const urlPostCode = searchParams?.postcode;
  const selected = data?.customizeCar?.selectedFeatures?.selected;
  const nextFeatureSelectionId = useReactiveVar(nextFeatureSelectionIdVar);
  const noModal = searchParams?.noModal;
  const [toastDisplayed, setToastDisplayed] = useState(false);

  const toggleFeature = useCallback(
    (code: string) => {
      if (!currentSelectedFeatures.current?.includes(code)) {
        setOptimisticSelectedFeatures(prev => [...prev, code]);
      } else {
        setOptimisticSelectedFeatures(prev => prev.filter(feature => feature !== code));
      }
    },
    [currentSelectedFeatures],
  );

  useEffect(() => {
    if (!loading && data) {
      setToastDisplayed(false);
      currentSelectedFeatures.current = data.customizeCar?.selectedFeatures.selected;
      currentCarCode.current = data.customizeCar?.car.code;

      if (!toastDisplayed && nextFeatureSelectionId && !selected.includes(nextFeatureSelectionId)) {
        toast.info(
          intl.formatMessage({
            id: 'trans__configurator__customise_page_interior_section__message_incompatible',
          }),
          {},
        );
        setToastDisplayed(true);
      }
    }
  }, [data, loading]);

  useEffect(() => {
    if (!loading && customizeCarError) {
      const hasStockLock = stockLockService.hasStockLock(customizeCarError);
      if (hasStockLock) {
        !noModal && setShowStockLockModal(true);
      } else {
        const isPostcodeError = !!customizeCarError.graphQLErrors[0]?.extensions?.invalid_postcode;
        if (!isPostcodeError) {
          setError(customizeCarError);
        }
      }
      return;
    }

    if (data?.customizeCar && !loading && !isSeachCarLoading) {
      const carCode = data.customizeCar?.car?.code;

      setSelectedCar(data.customizeCar);
      syncUrlSearchParams({
        [UrlSearchParamType.CAR_CODE]: carCode,
        [UrlSearchParamType.SELECTED_FEATURES]: selected,
      });
      currentCarCode.current = carCode;

      if (!areEqual(selected, optimisticSelectedFeatures)) {
        setOptimisticSelectedFeatures(selected);
        currentSelectedFeatures.current = selected;
      }
    }
  }, [data, loading, isSeachCarLoading, syncUrlSearchParams, customizeCarError]);

  useEffect(() => {
    if (
      searchCar &&
      !isSeachCarLoading &&
      !customizeCarError &&
      (currentCarCode.current !== searchCar ||
        !areEqual(currentSelectedFeatures.current, optimisticSelectedFeatures) ||
        (!!financeFormInput &&
          selectedCar?.finance.type !== FinanceResultType.error &&
          !deepEqual(financeFormInput, initialFinanceFormInput)) ||
        (!!urlPostCode && !!postCode && postCode !== urlPostCode) ||
        !deepEqual(initialFinanceFormInput, currentInsuranceFormInput))
    ) {
      const colorFeatureCodes = getColorFeatureCodes(
        selectedCar?.availableFeatureCategories,
        selectedCar?.availableFeatures,
      );
      const isColorChanged =
        colorFeatureCodes.filter(featureCode => optimisticSelectedFeatures.includes(featureCode)).length > 2;
      const isVariantChanged = currentCarCode.current !== searchCar;
      const byoCode = selectedCar?.car?.byoCode;

      const carId = getCarId({
        code: searchCar,
        byoCode,
        isColorChanged,
        isVariantChanged,
      });

      const vehicleConfigurationUrl = isInSalesEnablement
        ? window.location.origin +
          createBasePath(basePath) +
          createVehicleConfigurationUrl(
            {
              [UrlSearchParamType.CAR_CODE]: carId,
              [UrlSearchParamType.SELECTED_FEATURES]: optimisticSelectedFeatures,
            },
            searchParams,
          )
        : null;

      const cancelRequest = loadCustomizeCar(
        {
          carId,
          postCode,
          selectedFeatureIds: {
            selected: currentSelectedFeatures.current || optimisticSelectedFeatures,
            added: optimisticSelectedFeatures.filter(feature => !currentSelectedFeatures.current?.includes(feature)),
            removed:
              currentSelectedFeatures.current?.filter(feature => !optimisticSelectedFeatures.includes(feature)) || [],
          },
          insuranceFormInput: insuranceFormData || null,
          financeFormInput,
          financeInput,
          vehicleConfigurationUrl,
        },
        () => {
          setInitialFinanceFormInput(financeFormInput);
          setCurrentInsuranceFormInput(insuranceFormData);
        },
      );

      return () => {
        if (
          areEqual(currentSelectedFeatures.current, optimisticSelectedFeatures) &&
          deepEqual(financeFormInput, initialFinanceFormInput)
        ) {
          return;
        }
        if (!deepEqual(initialFinanceFormInput, currentInsuranceFormInput)) {
          return;
        }
        if (typeof currentCarCode.current === typeof searchCar && currentCarCode.current !== searchCar) {
          return;
        }
        if (searchCar && !currentCarCode.current) {
          return;
        }
        cancelRequest();
      };
    }
  }, [
    searchCar,
    isSeachCarLoading,
    financeFormInput,
    postCode,
    insuranceFormData,
    currentInsuranceFormInput,
    initialFinanceFormInput,
    searchCar,
    currentSelectedFeatures,
    optimisticSelectedFeatures,
  ]);

  return (
    <CustomizeCarContext.Provider
      value={{
        selectedCar,
        optimisticSelectedFeatures,
        error,
        loading,
        loadCustomizeCar,
        toggleFeature,
      }}
    >
      {children}
    </CustomizeCarContext.Provider>
  );
};

export default CustomizeCarContextProvider;
